package pl.nickers.match3

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Switch
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp

/**
 * App version shown on the about line. ASSUMPTION: the project has no build-injected
 * version string reachable from commonMain (no BuildConfig-style plugin wired up), so
 * this is a hand-maintained literal rather than something read from Gradle.
 */
private const val APP_VERSION = "0.1.0"

/**
 * F16.3 — Settings screen (issue #110): sound toggle, progress reset with
 * confirmation, and an about line. Reachable from the Start screen's gear icon.
 *
 * No "replay tutorial" control here: the level-1 tutorial (see [pl.nickers.match3.ecs.TutorialComponent])
 * now shows on every play of level 1, not just a player's first attempt, so there is
 * nothing to reset — playing (or replaying) level 1 is itself the "replay" action.
 */
@Composable
fun SettingsScreen(
    storage: GameStorage,
    journeyService: JourneyService?,
    onBack: () -> Unit,
) {
    var soundEnabled by remember { mutableStateOf(storage.soundEnabled) }
    var showResetConfirm by remember { mutableStateOf(false) }
    var progressResetMessage by remember { mutableStateOf(false) }

    Column(
        modifier = Modifier
            .fillMaxSize()
            .background(MaterialTheme.colorScheme.surface.copy(alpha = 0.92f))
            .padding(16.dp)
            .verticalScroll(rememberScrollState()),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            TextButton(onClick = onBack) { Text("← Back") }
        }

        Spacer(Modifier.height(8.dp))

        Text(
            text = "Settings",
            fontSize = 32.sp,
            fontWeight = FontWeight.Bold,
            color = MaterialTheme.colorScheme.primary,
        )

        Spacer(Modifier.height(24.dp))
        SectionHeader("Audio")
        Row(
            modifier = Modifier.fillMaxWidth().padding(vertical = 6.dp),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text("Sound")
            Switch(
                checked = soundEnabled,
                onCheckedChange = {
                    soundEnabled = it
                    storage.soundEnabled = it
                },
            )
        }

        Spacer(Modifier.height(24.dp))
        SectionHeader("Progress")
        OutlinedButton(
            onClick = { showResetConfirm = true },
            enabled = journeyService != null,
            modifier = Modifier.fillMaxWidth(),
        ) {
            Text("Reset progress")
        }
        if (progressResetMessage) {
            Spacer(Modifier.height(4.dp))
            Text(
                "Journey progress has been reset.",
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                fontSize = 13.sp,
            )
        }

        Spacer(Modifier.height(24.dp))
        SectionHeader("About")
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
        ) {
            Text("Jelly Match", fontWeight = FontWeight.SemiBold)
            Text("v$APP_VERSION", color = MaterialTheme.colorScheme.onSurfaceVariant)
        }
    }

    if (showResetConfirm) {
        AlertDialog(
            onDismissRequest = { showResetConfirm = false },
            title = { Text("Reset progress?") },
            text = { Text("This clears your journey unlock progress, star ratings, and any in-progress level. This cannot be undone.") },
            confirmButton = {
                TextButton(onClick = {
                    journeyService?.resetProgress()
                    showResetConfirm = false
                    progressResetMessage = true
                }) { Text("Reset") }
            },
            dismissButton = {
                TextButton(onClick = { showResetConfirm = false }) { Text("Cancel") }
            },
        )
    }
}

@Composable
private fun SectionHeader(title: String) {
    Column(modifier = Modifier.fillMaxWidth()) {
        Text(
            text = title,
            fontSize = 18.sp,
            fontWeight = FontWeight.Bold,
            color = MaterialTheme.colorScheme.secondary,
        )
        Spacer(Modifier.height(4.dp))
        HorizontalDivider()
        Spacer(Modifier.height(8.dp))
    }
}
